<script lang="ts">
  // libs
  import * as _ from "lodash";
  import { authorizationCheck } from "@shared/svelte/Modules/AuthorizationCheck.svelte";
  import * as LocaleString from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";

  // componenets
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import EmptyTable from "@shared/svelte/Components/EmptyTable.svelte";

  // types
  import type { Invoice } from "@shared/types/Invoice";

  // props
  export let invoices: Invoice[];
  export let viewInvoice: (invoiceId: number) => void;
  export let cancelScheduledPayment: (invoiceId: number) => void;
  export let handleScheduledPaymentModification: (invoice: Invoice) => void;
  export let searchRan: boolean;
  export let showLoadingState: boolean;
  export let selectedInvoices: Invoice[] = [];

  // data
  const loadingState = window["C"].IMAGE_PATHS["loadertransparent.gif"];
  let invoiceStatusClass = {
    unpaid: "fizz-pill-gray",
    overdue: "fizz-pill-red",
    failed: "fizz-pill-red",
  };

  const daysOverdueText = (invoice: Invoice) =>
    invoice.days_overdue == 1 ? `${invoice.days_overdue} day` : `${invoice.days_overdue} days`;

  const checkboxDisabled = (invoice: Invoice) => {
    return (
      !authorizationCheck("invoices.pay") ||
      parseFloat(invoice.amount_due) <= 0 ||
      invoice.is_read_only ||
      !invoice.is_payable
    );
  };

  // functions
  function goToInvoice(event: any, invoiceId: number) {
    let target = event.target;
    let skipLabels = ["Distributor", "Due Date", "Status", "Invoice", "Amount"];

    // don't redirect if user clicks on the invoice menu button section
    let targetLabel = skipLabels.includes(target.dataset.label);
    let skipTargetClass = target.classList.contains("popover-list-item");
    if (!targetLabel || skipTargetClass) return;

    viewInvoice(invoiceId);
  }

  function updateInvoiceSelection(invoice: Invoice) {
    selectedInvoices.some(i => i.id == invoice.id)
      ? (selectedInvoices = selectedInvoices.filter(i => i.id != invoice.id))
      : selectedInvoices.push(invoice);
    selectedInvoices = selectedInvoices;
  }

  function displayStatusType(invoice: Invoice): string {
    if (invoiceOverdue(invoice)) {
      return "overdue";
    } else if (invoiceUnpaid(invoice)) {
      return "unpaid";
    } else {
      return invoice.invoice_status_type;
    }
  }

  function invoiceOverdue(invoice: Invoice): boolean {
    return unpaidOrScheduled(invoice.invoice_status_type) && invoice.days_overdue > 0;
  }

  function invoiceUnpaid(invoice: Invoice): boolean {
    return unpaidOrScheduled(invoice.invoice_status_type) && invoice.days_overdue == 0;
  }

  function unpaidOrScheduled(status: string): boolean {
    return status === "unpaid" || status === "scheduled";
  }

  function invoiceScheduled(invoice: Invoice): boolean {
    return invoice.payment_scheduled_for !== null;
  }

  function toggleInvoiceDetail(e: MouseEvent, selectedIndex: number): void {
    let buttonQuery = 'button[class*="invoice_button_"].fizz-is-active';
    let menuButton = e.currentTarget as HTMLElement;
    menuButton.classList.toggle("fizz-is-active");

    for (let button of document.querySelectorAll(buttonQuery)) {
      if (button.classList.contains("invoice_button_" + selectedIndex)) {
        continue;
      }
      button.classList.remove("fizz-is-active");
    }
  }

  function isRowChecked(invoice: Invoice): boolean {
    return selectedInvoices.some(inv => inv.id == invoice.id);
  }
</script>

<style>
  .sticky-header th {
    position: sticky;
    top: -1px;
    z-index: 10;
    background-color: #ffffff;
  }
  .loading-state {
    margin-left: -5px;
    height: 30px;
    width: 30px;
  }
</style>

<thead class="sticky-header">
  <tr>
    <th style="width:12px" />
    <th>Distributor</th>
    <th>Due Date</th>
    <th width="25%">Status</th>
    <th width="20%">Invoice</th>
    <th class="fizz-text-right">Amount</th>
    <th width="1%" />
  </tr>
</thead>
<tbody>
  {#if showLoadingState && invoices.length == 0}
    <tr class="fizz-text-center alt-state-height">
      <td class="layout-1" colspan={8} style="padding-top: 8vh;">
        <div class="fizz-flex-row fizz-justify-center">
          <img alt="loader" src={loadingState} class="loading-state" />
        </div>
      </td>
    </tr>
  {:else if invoices.length > 0}
    {#each invoices as invoice, i}
      <tr
        on:click={e => goToInvoice(e, invoice.id)}
        id="invoice-{invoice.id}"
        class="cursor-pointer"
      >
        <td class="fizz-row-select">
          <input
            type="checkbox"
            name="checkbox"
            id="checkbox-{invoice.id}"
            class="fizz-checkbox"
            checked={isRowChecked(invoice)}
            disabled={checkboxDisabled(invoice)}
            on:click={() => updateInvoiceSelection(invoice)}
          />
        </td>
        <td data-label="Distributor">{invoice.distributor.name}</td>
        <td data-label="Due Date">
          <div class="fizz-flex-row-0">
            <span id="due-date-{invoice.id}">
              {LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(invoice.due_date)}
            </span>
            {#if invoiceScheduled(invoice)}
              <span>
                <div class="fizz-tooltip-container fizz-flex-row-8">
                  <button
                    class="fizz-tooltip-trigger"
                    aria-describedby="scheduled-by-tooltip-{invoice.id}"
                  >
                    <Icon
                      type="ClockAfternoon"
                      height={13}
                      width={13}
                      id="scheduled-clock-{invoice.id}"
                    />
                  </button>
                  <div
                    class="fizz-tooltip fizz-tooltip-left"
                    role="tooltip"
                    id="scheduled-by-tooltip-{invoice.id}"
                  >
                    Scheduled for {LocaleString.convertISODateStringToTimeZoneSafeFormattedLocaleDate(
                      invoice.payment_scheduled_for,
                    )}
                  </div>
                </div>
              </span>
            {/if}
          </div>
        </td>
        <td data-label="Status">
          <span
            class={invoiceStatusClass[displayStatusType(invoice)]}
            style="text-transform:capitalize;"
          >
            {displayStatusType(invoice)}
          </span>
          {#if invoiceOverdue(invoice)}
            <span class="fizz-text-medium fizz-text-urgent">{daysOverdueText(invoice)}</span>
          {/if}
        </td>
        <td data-label="Invoice">#{invoice.invoice_reference_id}</td>
        <td data-label="Amount" class="fizz-align-right">
          <FormatMoney input={invoice.amount_due} formatType={"5"} />
        </td>
        <td>
          <div class="fizz-table-menu">
            <button
              on:click|preventDefault={e => toggleInvoiceDetail(e, i)}
              class="fizz-icon-button-small invoice_button_{i}"
            >
              <Icon className="fizz-icon-16" type="MoreVertical" />
            </button>
            <ul class="fizz-popover">
              <li>
                <a
                  class="fizz-nowrap"
                  href={"#"}
                  on:click|preventDefault={() => viewInvoice(invoice.id)}>View details</a
                >
              </li>
              {#if invoice.invoice_status_type == "scheduled"}
                <li>
                  <a
                    class="fizz-nowrap"
                    href={"#"}
                    on:click|preventDefault={() => cancelScheduledPayment(invoice.id)}
                    >Cancel scheduled payment</a
                  >
                </li>
                <li>
                  <a
                    class="fizz-nowrap"
                    href={"#"}
                    on:click|preventDefault={() => handleScheduledPaymentModification(invoice)}
                    >Modify scheduled payment</a
                  >
                </li>
              {/if}
            </ul>
          </div>
        </td>
      </tr>
    {/each}
  {:else}
    <tr class="fizz-text-center">
      <td colspan={8}>
        {#if searchRan}
          <div class="padding--small">Nothing matches your search.</div>
        {:else}
          <div class="fizz-surface fizz-full-height">
            <EmptyTable iconType="MoneyOff" heading="Nice work! No outstanding invoices.">
              <p id="distributor-list" class="fizz-text-medium">
                There are no outstanding invoices available on Provi.
              </p>
            </EmptyTable>
          </div>
        {/if}
      </td>
    </tr>
  {/if}
</tbody>
